import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { LegalPageShell } from '../../components/Layout/LegalPageShell';

export function CookiePolicy() {
  const { t } = useTranslation();
  return (
    <LegalPageShell
      title={t('legal.cookiePolicy.title')}
      lastUpdated={t('legal.cookiePolicy.lastUpdated')}
    >
              <section className="mb-8">
                <h2 className="text-2xl font-semibold text-room-text mb-4">
                  1. Introduction
                </h2>
                <p className="text-room-text-dim mb-4">
                  This Cookie Policy explains how CalendIn™ uses cookies and similar storage technologies when you use our service. By using CalendIn™, you consent to the use of cookies and local storage as described in this policy.
                </p>
                <p className="text-room-text-dim">
                  <strong>Important:</strong> CalendIn currently does NOT use traditional cookies. Instead, we use browser localStorage for essential functionality. This policy explains our current practices and planned future analytics implementation.
                </p>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-semibold text-room-text mb-4">
                  2. What Are Cookies and Local Storage?
                </h2>
                
                <h3 className="text-xl font-semibold text-room-text mb-3 mt-6">
                  2.1 Cookies
                </h3>
                <p className="text-room-text-dim mb-4">
                  Cookies are small text files that websites store on your device to remember information about you. They enable features like user preferences, session management, and analytics.
                </p>

                <h3 className="text-xl font-semibold text-room-text mb-3 mt-6">
                  2.2 Local Storage
                </h3>
                <p className="text-room-text-dim">
                  Local storage is a browser feature that allows websites to store data locally on your device. Unlike cookies, this data is not automatically sent to the server with every request. CalendIn uses local storage instead of cookies for essential functionality.
                </p>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-semibold text-room-text mb-4">
                  3. What We Currently Use
                </h2>
                
                <h3 className="text-xl font-semibold text-room-text mb-3 mt-6">
                  3.1 Essential Local Storage (Required)
                </h3>
                <p className="text-room-text-dim mb-4">
                  CalendIn uses browser localStorage to store one essential piece of information:
                </p>
                <div className="bg-gray-50 dark:bg-gray-700 p-4 rounded-lg mb-4">
                  <p className="text-room-text-dim mb-2">
                    <strong>Storage Key:</strong> <code className="bg-gray-200 dark:bg-gray-600 px-2 py-1 rounded">browser_fingerprint</code>
                  </p>
                  <p className="text-room-text-dim mb-2">
                    <strong>Purpose:</strong> Identifies your browser session to prevent duplicate participant names within the same room
                  </p>
                  <p className="text-room-text-dim mb-2">
                    <strong>Content:</strong> A 64-character base64-encoded string generated from your browser properties (user agent, language, screen size, timezone, etc.)
                  </p>
                  <p className="text-room-text-dim mb-2">
                    <strong>Duration:</strong> Persists until you manually clear your browser's local storage
                  </p>
                  <p className="text-room-text-dim">
                    <strong>Category:</strong> Strictly necessary for service functionality
                  </p>
                </div>
                <p className="text-room-text-dim">
                  This browser fingerprint is essential for CalendIn to function properly. Without it, you may be assigned duplicate names in rooms or experience other identity-related issues.
                </p>

                <h3 className="text-xl font-semibold text-room-text mb-3 mt-6">
                  3.2 No Tracking Cookies (Currently)
                </h3>
                <p className="text-room-text-dim">
                  CalendIn does NOT currently use:
                </p>
                <ul className="list-disc list-inside text-room-text-dim space-y-2">
                  <li>Advertising cookies</li>
                  <li>Analytics cookies (Google Analytics, etc.)</li>
                  <li>Social media cookies</li>
                  <li>Third-party tracking cookies</li>
                  <li>Marketing or retargeting cookies</li>
                </ul>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-semibold text-room-text mb-4">
                  4. Planned Future Use
                </h2>
                <p className="text-room-text-dim mb-4">
                  We plan to integrate analytics services in the future to better understand how users interact with CalendIn and improve the service. When we do, we may use:
                </p>

                <h3 className="text-xl font-semibold text-room-text mb-3 mt-6">
                  4.1 Analytics Cookies
                </h3>
                <p className="text-room-text-dim mb-4">
                  We may implement one of the following privacy-focused analytics solutions:
                </p>
                <ul className="list-disc list-inside text-room-text-dim space-y-2 mb-4">
                  <li><strong>Plausible Analytics:</strong> Privacy-friendly, no cookies, GDPR compliant</li>
                  <li><strong>Google Analytics:</strong> Industry-standard analytics with anonymized IPs</li>
                  <li><strong>Similar alternatives:</strong> Other privacy-respecting analytics tools</li>
                </ul>
                <p className="text-room-text-dim mb-4">
                  These analytics tools would help us understand:
                </p>
                <ul className="list-disc list-inside text-room-text-dim space-y-2 mb-4">
                  <li>How many people use CalendIn</li>
                  <li>Which features are most popular</li>
                  <li>How users navigate the service</li>
                  <li>Performance and error tracking</li>
                </ul>

                <h3 className="text-xl font-semibold text-room-text mb-3 mt-6">
                  4.2 When We Add Analytics
                </h3>
                <p className="text-room-text-dim mb-4">
                  If we add analytics tracking, we will:
                </p>
                <ul className="list-disc list-inside text-room-text-dim space-y-2 mb-4">
                  <li>Update this Cookie Policy with specific details</li>
                  <li>Update the "Last updated" date</li>
                  <li>Provide a way to opt out of analytics tracking</li>
                  <li>Choose the most privacy-friendly option available</li>
                  <li>Not use analytics for advertising or selling data</li>
                </ul>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-semibold text-room-text mb-4">
                  5. Third-Party Cookies
                </h2>
                <p className="text-room-text-dim mb-4">
                  CalendIn itself does not currently set third-party cookies. However, our hosting and infrastructure providers may use their own cookies:
                </p>
                <ul className="list-disc list-inside text-room-text-dim space-y-2 mb-4">
                  <li><strong>Vercel (Frontend Hosting):</strong> May use performance and analytics cookies</li>
                  <li><strong>Railway (Backend Hosting):</strong> May use infrastructure cookies</li>
                </ul>
                <p className="text-room-text-dim">
                  These third-party services have their own cookie policies. Please refer to their respective privacy policies for details.
                </p>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-semibold text-room-text mb-4">
                  6. How to Manage Cookies and Local Storage
                </h2>
                
                <h3 className="text-xl font-semibold text-room-text mb-3 mt-6">
                  6.1 Clear CalendIn's Local Storage
                </h3>
                <p className="text-room-text-dim mb-4">
                  To remove your browser fingerprint from CalendIn:
                </p>
                <div className="bg-gray-50 dark:bg-gray-700 p-4 rounded-lg mb-4">
                  <p className="text-room-text-dim mb-3">
                    <strong>Option 1: Browser Developer Tools</strong>
                  </p>
                  <ol className="list-decimal list-inside text-room-text-dim space-y-1 mb-4 ml-4">
                    <li>Open CalendIn in your browser</li>
                    <li>Press F12 or right-click and select "Inspect"</li>
                    <li>Go to the "Application" or "Storage" tab</li>
                    <li>Find "Local Storage" in the left sidebar</li>
                    <li>Click on the CalendIn domain</li>
                    <li>Delete the <code className="bg-gray-200 dark:bg-gray-600 px-1 py-0.5 rounded text-sm">browser_fingerprint</code> key</li>
                  </ol>

                  <p className="text-room-text-dim mb-3 mt-4">
                    <strong>Option 2: Clear All Site Data</strong>
                  </p>
                  <ol className="list-decimal list-inside text-room-text-dim space-y-1 ml-4">
                    <li>Open your browser settings</li>
                    <li>Go to Privacy & Security</li>
                    <li>Find "Clear browsing data" or "Site settings"</li>
                    <li>Select CalendIn's domain</li>
                    <li>Clear site data and cookies</li>
                  </ol>
                </div>
                <p className="text-room-text-dim">
                  <strong>Note:</strong> Clearing your browser fingerprint will cause CalendIn to generate a new one the next time you visit, which may result in duplicate participant entries in rooms you've previously joined.
                </p>

                <h3 className="text-xl font-semibold text-room-text mb-3 mt-6">
                  6.2 Browser Cookie Settings
                </h3>
                <p className="text-room-text-dim mb-4">
                  Most browsers allow you to control cookies through settings. Here's how to manage cookies in popular browsers:
                </p>
                <ul className="list-disc list-inside text-room-text-dim space-y-2">
                  <li><strong>Chrome:</strong> Settings → Privacy and security → Cookies and other site data</li>
                  <li><strong>Firefox:</strong> Settings → Privacy & Security → Cookies and Site Data</li>
                  <li><strong>Safari:</strong> Preferences → Privacy → Manage Website Data</li>
                  <li><strong>Edge:</strong> Settings → Privacy, search, and services → Cookies and site permissions</li>
                </ul>

                <h3 className="text-xl font-semibold text-room-text mb-3 mt-6">
                  6.3 Impact of Disabling Local Storage
                </h3>
                <p className="text-room-text-dim">
                  <strong>Warning:</strong> If you block or disable local storage, CalendIn will not function properly. The browser fingerprint is essential for identifying your session and preventing duplicate participants in rooms.
                </p>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-semibold text-room-text mb-4">
                  7. Do Not Track (DNT)
                </h2>
                <p className="text-room-text-dim">
                  CalendIn respects Do Not Track (DNT) browser settings. Since we don't currently use analytics or tracking cookies, DNT settings have no effect on our current implementation. If we add analytics in the future, we will honor DNT preferences when technically feasible.
                </p>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-semibold text-room-text mb-4">
                  8. Changes to This Cookie Policy
                </h2>
                <p className="text-room-text-dim mb-4">
                  We will update this Cookie Policy when we add analytics or make changes to our cookie practices. Updates will include:
                </p>
                <ul className="list-disc list-inside text-room-text-dim space-y-2 mb-4">
                  <li>Revised "Last updated" date</li>
                  <li>Clear description of new cookies or storage used</li>
                  <li>Instructions for opting out of new tracking</li>
                </ul>
                <p className="text-room-text-dim">
                  We encourage you to review this policy periodically for any updates.
                </p>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-semibold text-room-text mb-4">
                  9. More Information
                </h2>
                <p className="text-room-text-dim mb-4">
                  For more information about how we handle your data, please review our:
                </p>
                <ul className="list-disc list-inside text-room-text-dim space-y-2 mb-6">
                  <li><Link to="/privacy" className="text-room-accent hover:underline">Privacy Policy</Link> - Comprehensive data collection and usage details</li>
                  <li><Link to="/terms" className="text-room-accent hover:underline">Terms of Service</Link> - Usage rules and service terms</li>
                </ul>
                <p className="text-room-text-dim mb-4">
                  If you have questions about cookies, local storage, or this Cookie Policy, please contact us:
                </p>
                <div className="bg-gray-50 dark:bg-gray-700 p-4 rounded-lg">
                  <p className="text-room-text-dim">
                    <strong>Email:</strong> <a href="mailto:info@calendin.app" className="text-room-accent hover:underline">info@calendin.app</a>
                  </p>
                  <p className="text-room-text-dim mt-2">
                    <strong>Service:</strong> CalendIn
                  </p>
                </div>
              </section>
    </LegalPageShell>
  );
}

